import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Button } from "@/ui/components/ui/button";
import { 
  Download, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  FileSpreadsheet,
  Calendar,
  MapPin,
  BarChart3,
  Eye
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { ReportJob, JobStatus, JobType } from "@/export/types/reports.ts";
import { ReportProgress } from "./report-progress";
import { useDownloadReport } from "@/export/api/queries/reports.queries";
import { formatDateTime, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { formatRelativeTimeUTC } from "@/shared/utils/date/format-relative-time";

interface ReportJobCardProps {
  job: ReportJob;
  onDownload?: (jobId: string) => void;
  onShow?: (jobId: string) => void;
  className?: string;
}

const getStatusIcon = (status: JobStatus) => {
  switch (status) {
    case 'PENDING':
      return <Clock className="h-4 w-4" />;
    case 'RUNNING':
      return <Loader2 className="h-4 w-4 animate-spin" />;
    case 'SUCCESS':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'FAILED':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return <Clock className="h-4 w-4" />;
  }
};

const getStatusColor = (status: JobStatus) => {
  switch (status) {
    case 'PENDING':
      return 'text-yellow-600';
    case 'RUNNING':
      return 'text-blue-600';
    case 'SUCCESS':
      return 'text-green-600';
    case 'FAILED':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};

type ReportTypeKey =
  | "reportType.bouldersExport"
  | "reportType.activityExport"
  | "reportType.generic";

const getReportTypeKey = (type: JobType): ReportTypeKey => {
  switch (type) {
    case 'boulders_report':
      return "reportType.bouldersExport";
    case 'activity_report':
      return "reportType.activityExport";
    default:
      return "reportType.generic";
  }
};

const formatJobDateTime = (dateString: string) => {
  try {
    return formatDateTime(new Date(dateString));
  } catch {
    return dateString;
  }
};

const getDeviceNames = (job: ReportJob, unknownLabel: string): string => {
  if (job.result_summary?.selection?.devices_detailed) {
    return job.result_summary.selection.devices_detailed
      .map(device => device.device_name)
      .join(', ');
  }
  
  if (job.result_summary?.selection?.devices) {
    return job.result_summary.selection.devices.join(', ');
  }
  
  return unknownLabel;
};

export const ReportJobCard: React.FC<ReportJobCardProps> = ({
  job,
  onDownload,
  onShow,
  className,
}) => {
  const { t } = useTranslation("export");
  const { downloadReport, isDownloading } = useDownloadReport();

  const canDownload = job.status === 'SUCCESS' && job.artifact?.exists;
  const fileSize = job.artifact?.size_bytes
    ? t("job.fileSize", {
        size: formatNumber(job.artifact.size_bytes / 1024 / 1024, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      })
    : t("job.unknownSize");

  const handleDownload = () => {
    if (onDownload) {
      onDownload(job.id);
    } else {
      downloadReport(job.id);
    }
  };

  const handleShow = () => {
    if (onShow) {
      onShow(job.id);
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileSpreadsheet className="h-5 w-5" />
          <span>{t(getReportTypeKey(job.type))}</span>
          <div className="flex items-center gap-1 ml-auto">
            {getStatusIcon(job.status)}
            <span className={`text-sm font-medium ${getStatusColor(job.status)}`}>
              {t(`status.${job.status}`, { defaultValue: job.status })}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Progress */}
        <ReportProgress 
          progress={job.progress} 
          status={job.status}
        />
        
        {/* Export Details */}
        {job.result_summary && (
          <div className="space-y-3 border-t pt-3">
            {/* Time Range */}
            {job.result_summary.window && (
              <div className="flex items-start gap-2 text-sm">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <span className="text-muted-foreground">{t("job.timeRangeLabel")}</span>
                  <div className="font-medium">
                    {formatJobDateTime(job.result_summary.window.start)} - {formatJobDateTime(job.result_summary.window.end)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {job.result_summary.window.timezone}
                  </div>
                </div>
              </div>
            )}

            {/* Devices */}
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">{t("job.machinesLabel")}</span>
                <div className="font-medium">
                  {getDeviceNames(job, t("job.unknownDevices"))}
                </div>
              </div>
            </div>

            {/* Results */}
            {job.result_summary.outcome && (
              <div className="flex items-start gap-2 text-sm">
                <BarChart3 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <span className="text-muted-foreground">{t("job.resultsLabel")}</span>
                  <div className="font-medium">
                    {job.result_summary.outcome === 'SUCCESS' ? (
                      <div className="space-y-1">
                        <div>
                          {t("job.totalRows", {
                            count: job.result_summary.counts.total,
                          })}
                        </div>
                        {Object.entries(job.result_summary.counts.datasets).map(([dataset, count]) => (
                          <div key={dataset} className="text-xs text-muted-foreground">
                            {t("job.datasetRows", { dataset, count })}
                          </div>
                        ))}
                      </div>
                    ) : (
                      t("job.noData", {
                        reason:
                          job.result_summary.reason_code ||
                          t("job.reasonEmptyWindow"),
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Job Details */}
        <div className="grid grid-cols-2 gap-4 text-sm border-t pt-3">
          <div>
            <span className="text-muted-foreground">{t("job.createdLabel")}</span>
            <div className="font-medium">
              {formatRelativeTimeUTC(job.created_at)}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">{t("job.updatedLabel")}</span>
            <div className="font-medium">
              {formatRelativeTimeUTC(job.updated_at)}
            </div>
          </div>
        </div>

        {/* Artifact Info */}
        {job.artifact?.exists && (
          <div className="border-t pt-3">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="text-muted-foreground">{t("job.fileSizeLabel")}</span>
                <span className="ml-1 font-medium">{fileSize}</span>
              </div>
              {canDownload && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleShow}
                    className="flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {t("job.show")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {isDownloading ? t("job.downloading") : t("job.download")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Job ID */}
        <div className="text-xs text-muted-foreground border-t pt-3">
          {t("job.jobId", { id: job.id })}
        </div>
      </CardContent>
    </Card>
  );
};
